import { Table } from "antd";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import useTableOverflowControls from "./useTableOverflowControls";

const SCROLL_STEP = 18;
const ACTION_COLUMN_WIDTH = 86;
const MIN_SCROLL_Y = 220;
const OVERFLOW_TOLERANCE = 8;
const SAFETY_GAP = 8;

// The table may live inside an app shell that scrolls instead of the window.
const getScrollParent = (node) => {
  let element = node?.parentElement;

  while (element && element !== document.body && element !== document.documentElement) {
    const overflowY = window.getComputedStyle(element).overflowY;
    if (/(auto|scroll|overlay)/.test(overflowY)) return element;
    element = element.parentElement;
  }

  return null;
};

const prettifyColumnKey = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatMessageId = (intl, id) => {
  if (!id) return "";

  return intl?.messages?.[id] ? intl.formatMessage({ id }) : "";
};

const getTitleText = (title, intl) => {
  if (title === null || title === undefined) return "";
  if (typeof title === "string" || typeof title === "number") return String(title);
  if (Array.isArray(title)) return title.map((item) => getTitleText(item, intl)).join(" ").trim();
  if (React.isValidElement(title)) {
    const translatedId = formatMessageId(intl, title.props?.id);
    if (translatedId) return translatedId;

    const childText = getTitleText(title.props?.children, intl);
    if (childText) return childText;

    return getTitleText(title.props?.defaultMessage, intl);
  }
  return "";
};

const normalizeDataLabel = (column, index, intl) => {
  const explicitTitle = getTitleText(column?.title, intl).trim();
  if (explicitTitle) return explicitTitle;

  if (Array.isArray(column?.dataIndex)) {
    return prettifyColumnKey(column.dataIndex.join(" "));
  }

  const keyLabel = getTitleText(column?.key, intl).trim();
  if (keyLabel) return keyLabel;

  return prettifyColumnKey(column?.dataIndex || column?.key || `Column ${index + 1}`);
};

const isActionColumn = (column, intl) => {
  const signature = [
    getTitleText(column?.key, intl),
    column?.dataIndex,
    getTitleText(column?.title, intl),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return signature.includes("action");
};

const BaseTable = (props) => {
  const { columns = [], loader, modkey, containerClassName, rowClassName, ...otherProps } = props;
  void modkey;
  const intl = useIntl();

  const shellRef = useRef(null);
  const scrollHostRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(null);

  const measureScrollY = useCallback(() => {
    const shell = shellRef.current;
    if (!shell || typeof window === "undefined") return;

    const shellRect = shell.getBoundingClientRect();
    const scrollParent = getScrollParent(shell);
    const viewportBottom = Math.min(
      scrollParent ? scrollParent.getBoundingClientRect().bottom : window.innerHeight,
      window.innerHeight
    );

    // Everything rendered under the table (pagination, paddings). This is invariant
    // to the cap itself — capping moves the shell's bottom and the page bottom together.
    const pageRoot =
      shell.closest(".reports-all__panel")
      || shell.closest(".base-body__content")
      || shell.closest(".base-body");
    const below = pageRoot
      ? Math.max(pageRoot.getBoundingClientRect().bottom - shellRect.bottom, 0)
      : 0;

    // Math.min keeps the cap sane if a measurement lands while the page is
    // scrolled down and the shell's viewport-relative top has gone negative.
    const available = Math.max(
      Math.round(
        Math.min(
          viewportBottom - shellRect.top - below,
          window.innerHeight - below
        ) - SAFETY_GAP
      ),
      MIN_SCROLL_Y
    );

    // scrollHeight is the natural content height whether or not a cap is applied,
    // so this decision stays stable instead of flip-flopping with the observer.
    const content = scrollHostRef.current?.querySelector(".ant-table-content");
    const naturalHeight = content?.scrollHeight || 0;
    const next =
      naturalHeight > available + OVERFLOW_TOLERANCE ? `${available}px` : null;

    setMaxHeight((current) => (current === next ? current : next));
  }, []);

  const isCapped = Boolean(maxHeight);

  useLayoutEffect(() => {
    measureScrollY();
  }, [measureScrollY, props.dataSource]);

  useEffect(() => {
    window.addEventListener("resize", measureScrollY);

    // The table's offset also shifts when filters/headers above it expand or collapse.
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measureScrollY) : null;
    observer?.observe(document.body);

    return () => {
      window.removeEventListener("resize", measureScrollY);
      observer?.disconnect();
    };
  }, [measureScrollY]);

  const {
    containerRef,
    hasOverflow,
    showLeftHint,
    showRightHint,
  } = useTableOverflowControls({
    dataSource: otherProps.dataSource,
    columns,
    enabled: true,
    step: SCROLL_STEP,
  });

  const normalizedColumns = useMemo(
    () =>
      columns.map((column, index) => {
        const dataLabel = normalizeDataLabel(column, index, intl);
        const actionColumn = isActionColumn(column, intl);

        return {
          ...column,
          fixed: actionColumn ? (column.fixed || "right") : column.fixed,
          width: actionColumn ? (column.width || ACTION_COLUMN_WIDTH) : column.width,
          ellipsis: actionColumn ? false : (column.ellipsis === false ? false : column.ellipsis || { showTitle: true }),
          onCell: (...args) => {
            const original = column.onCell?.(...args) || {};
            const originalClassName = original.className || "";

            return {
              ...original,
              title: original.title || (typeof args?.[0]?.[column?.dataIndex] === "string" ? args[0][column.dataIndex] : undefined),
              "data-label": original["data-label"] || dataLabel,
              className: [
                originalClassName,
                "base-table-cell",
                actionColumn ? "base-table-cell--actions" : "",
              ]
                .filter(Boolean)
                .join(" "),
            };
          },
          onHeaderCell: (...args) => {
            const original = column.onHeaderCell?.(...args) || {};
            return {
              ...original,
              "data-label": dataLabel,
              className: [
                original.className,
                "base-table-header-cell",
                actionColumn ? "base-table-header-cell--actions" : "",
              ]
                .filter(Boolean)
                .join(" "),
            };
          },
        };
      }),
    [columns, intl]
  );

  const mergedScroll = useMemo(
    () => ({
      ...otherProps.scroll,
      x: otherProps.scroll?.x || "max-content",
    }),
    [otherProps.scroll]
  );

  const composedRowClassName = (record, index) => {
    const inheritedClassName =
      typeof rowClassName === "function" ? rowClassName(record, index) : rowClassName;

    return ["base-table-row", inheritedClassName].filter(Boolean).join(" ");
  };

  return (
    <div
      className={ [
        "basetable-container",
        hasOverflow ? "basetable-container--overflowing" : "",
        isCapped ? "basetable-container--capped" : "",
        containerClassName,
      ]
        .filter(Boolean)
        .join(" ") }
    >
      <div className="basetable-shell" ref={ shellRef }>
        { hasOverflow ? (
          <>
            <div
              className={ [
                "basetable-edge basetable-edge--left",
                showLeftHint ? "is-visible" : "",
              ]
                .filter(Boolean)
                .join(" ") }
            />
            <div
              className={ [
                "basetable-edge basetable-edge--right",
                showRightHint ? "is-visible" : "",
              ]
                .filter(Boolean)
                .join(" ") }
            />
          </>
        ) : null }

        <div
          className="basetable-scroll-host"
          ref={ (node) => {
            scrollHostRef.current = node;
            containerRef.current = node;
          } }
          style={ maxHeight ? { "--base-table-max-height": maxHeight } : undefined }
        >
          <Table
            rowKey="id"
            size="middle"
            { ...otherProps }
            className={ ["base-table", otherProps.className].filter(Boolean).join(" ") }
            columns={ normalizedColumns }
            rowClassName={ composedRowClassName }
            showSorterTooltip={ false }
            tableLayout={ otherProps.tableLayout || "fixed" }
            loading={ loader }
            scroll={ mergedScroll }
            sticky={ otherProps.sticky || false }
            pagination={ false }
            onRow={ otherProps.onRow }
          />
        </div>
      </div>
    </div>
  );
};

export default BaseTable;
